/**
 * Ed Hardy Swim — story circles.
 *
 * Tapping a circle expands that category's products *inline, directly below the
 * rail*, rather than taking over the screen. That is the whole point of the
 * circles: tap Dresses, dresses appear underneath.
 *
 * Sub-categories appear as chips (Bikinis splits into Two Piece / One Piece).
 * Sold-out and last-size products are excluded, so a lane never looks picked
 * over.
 */

import { loadCatalog, bestsellerRank, sorter } from '@eh/catalog';
import { cardHTML, bindQuickAdd, sized } from '@eh/card';

const root = () => window.Shopify?.routes?.root || '/';

class EhStories extends HTMLElement {
  connectedCallback() {
    this.lanes = JSON.parse(this.querySelector('[data-eh-lanes]').textContent);
    this.showThin = this.dataset.showThin === 'true';
    this.limit = parseInt(this.dataset.limit || '8', 10);
    this.panel = this.querySelector('[data-eh-panel]');
    this.index = -1;
    this.sub = 'all';

    this.querySelectorAll('[data-eh-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.toggle(Number(btn.dataset.ehOpen)));
    });

    this.ready = Promise.all([
      loadCatalog(),
      bestsellerRank(this.dataset.rankFrom || 'shop-all-swim'),
    ]).then(([catalog, rank]) => {
      this.catalog = catalog;
      this.rank = rank;
      this.paintCircles();
    });

    /* Open the first lane on load when asked, so the homepage is never a row of
       circles with dead space underneath. */
    if (this.dataset.openFirst === 'true' && this.lanes.length) {
      this.toggle(0);
    }
  }

  /**
   * Replace each circle's image with the best-selling in-stock product from the
   * lane it actually opens. The Liquid fallback uses the linked collection's
   * artwork, which frequently has nothing to do with the category — a Cover Ups
   * circle showing a bikini, for instance. `used` stops two lanes picking the
   * same shot.
   */
  paintCircles() {
    const used = new Set();
    this.lanes.forEach((lane, i) => {
      const holder = this.querySelector(`[data-eh-open="${i}"] .eh-stories__thumb`);
      if (!holder) return;

      const ranked = this.catalog
        .filter((p) => {
          if (p.thin || !p.image) return false;
          if (lane.onSale) return p.onSale;
          if (lane.cats && lane.cats.length) return lane.cats.includes(p.cat);
          return p.cat !== 'kids' && p.cat !== 'other';
        })
        .sort(sorter(this.rank || new Map()));

      const pick = ranked.find((p) => !used.has(p.image)) || ranked[0];
      if (!pick) return;
      used.add(pick.image);

      holder.innerHTML = `<img src="${sized(pick.image, 260)}" alt="" loading="lazy" width="130" height="130">`;
    });
  }

  async toggle(i) {
    if (this.index === i) return this.close();
    this.index = i;
    this.sub = 'all';
    this.markActive();
    this.panel.hidden = false;
    this.panel.innerHTML = '<p class="eh-stories__loading">Loading…</p>';
    await this.ready;
    this.render();
    if (this.dataset.openFirst !== 'true' || this._opened) {
      this.panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    this._opened = true;
  }

  close() {
    this.index = -1;
    this.panel.hidden = true;
    this.panel.innerHTML = '';
    this.markActive();
  }

  markActive() {
    this.querySelectorAll('[data-eh-open]').forEach((b) => {
      const on = Number(b.dataset.ehOpen) === this.index;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-expanded', String(on));
    });
  }

  items() {
    if (!this.catalog) return [];
    const lane = this.lanes[this.index];
    let out = this.catalog.filter((p) => {
      if (!this.showThin && p.thin) return false;
      if (lane.cats && lane.cats.length && !lane.cats.includes(p.cat)) return false;
      if (lane.onSale && !p.onSale) return false;
      if (!lane.onSale && !(lane.cats || []).includes('kids') && p.cat === 'kids') return false;
      return true;
    });
    if (this.sub !== 'all') out = out.filter((p) => p.group === this.sub || p.cat === this.sub);
    if (this.rank) out.sort(sorter(this.rank));
    return out;
  }

  render() {
    const lane = this.lanes[this.index];
    const all = this.items();
    const shown = all.slice(0, this.limit);

    /*
     * "Shop all dresses" should land on the collection already filtered to
     * dresses, not on the full grid — the same ?cat= link an ad would use.
     * Only set it when the lane maps to exactly one category; "bikinis" spans
     * tops and bottoms, so it stays unfiltered unless the one-piece sub-filter
     * narrows it.
     */
    let shopAll = lane.url && lane.url !== '#' ? lane.url : null;
    if (shopAll) {
      let cat = lane.cats && lane.cats.length === 1 ? lane.cats[0] : null;
      if (this.sub === 'one-piece') cat = 'onepiece';
      if (cat) shopAll += `${shopAll.includes('?') ? '&' : '?'}cat=${encodeURIComponent(cat)}`;
    }

    this.panel.innerHTML = `
      <div class="eh-stories__panel-head">
        <h3 class="eh-stories__panel-title">${lane.label}</h3>
        <button type="button" class="eh-stories__panel-x" data-eh-close aria-label="Close ${lane.label}">
          <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M4 4l12 12M16 4L4 16" stroke="currentColor" stroke-width="1.6" fill="none"/></svg>
        </button>
      </div>

      ${
        lane.subs && lane.subs.length
          ? `<div class="eh-stories__chips">
              ${[{ value: 'all', label: 'All' }, ...lane.subs]
                .map(
                  (s) =>
                    `<button type="button" class="eh-chip${
                      this.sub === s.value ? ' is-active' : ''
                    }" data-sub="${s.value}">${s.label}</button>`
                )
                .join('')}
             </div>`
          : ''
      }

      <div class="eh-stories__grid">
        ${
          shown.length
            ? shown.map((p, i) => cardHTML(p, { width: 400, eager: i < 2 })).join('')
            : '<p class="eh-stories__loading">Nothing in stock here right now.</p>'
        }
      </div>

      ${
        shopAll
          ? `<a class="eh-stories__cta" href="${shopAll}">Shop all ${lane.label.toLowerCase()}${
              all.length > shown.length ? ` (${all.length})` : ''
            }</a>`
          : ''
      }
    `;

    this.panel.querySelector('[data-eh-close]').addEventListener('click', () => this.close());
    this.panel.querySelectorAll('[data-sub]').forEach((b) =>
      b.addEventListener('click', () => {
        this.sub = b.dataset.sub;
        this.render();
      })
    );
    bindQuickAdd(this.panel, (h) => this.catalog.find((x) => x.handle === h));
  }
}

if (!customElements.get('eh-stories')) customElements.define('eh-stories', EhStories);
